// Server-state hooks for the engine. The run hook implements the offline requirement
// (BR-006): when the API is unreachable it serves the bundled base-case snapshot and says so.
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import { endpoints } from '../lib/endpoints';
import type { Meta, RunRequest, RunResult } from '../types/api';
import { useDebounced } from './useDebounced';

interface Snapshot { generated_at: string; meta: Meta; run: RunResult }

let snapshotPromise: Promise<Snapshot | null> | null = null;
export function loadSnapshot(): Promise<Snapshot | null> {
  snapshotPromise ??= fetch('/snapshot.json', { cache: 'force-cache' })
    .then((r) => (r.ok ? (r.json() as Promise<Snapshot>) : null))
    .catch(() => null);
  return snapshotPromise;
}

export interface RunData { result: RunResult; offline: boolean; snapshotDate?: string }

export function useMeta(enabled = true) {
  return useQuery<{ meta: Meta; offline: boolean }, ApiError>({
    queryKey: ['meta'],
    staleTime: Infinity,
    enabled,
    queryFn: async ({ signal }) => {
      try {
        return { meta: await endpoints.meta(signal), offline: false };
      } catch (err) {
        if (err instanceof ApiError && err.isNetwork) {
          const snap = await loadSnapshot();
          if (snap) return { meta: snap.meta, offline: true };
        }
        throw err;
      }
    },
  });
}

export function useRun(request: RunRequest) {
  const debounced = useDebounced(request, 350);
  return useQuery<RunData, ApiError>({
    queryKey: ['run', debounced],
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      try {
        return { result: await endpoints.run(debounced, signal), offline: false };
      } catch (err) {
        if (err instanceof ApiError && err.isNetwork) {
          const snap = await loadSnapshot();
          if (snap) return { result: snap.run, offline: true, snapshotDate: snap.generated_at };
        }
        throw err;
      }
    },
  });
}
